import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { Textarea, TextareaInput } from "@/components/ui/textarea"
import { VStack } from "@/components/ui/vstack"
import { screenCopy } from "@/constants/Brand"
import { filterChipStyle } from "@/constants/ChipStyles"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { getDomain, getSkill } from "@/data/prooftree"
import { createEqJournalEntry } from "@/services/eqParityBackend"
import {
  EqEvidenceFile,
  pickProofEvidenceFile,
  validateEvidenceFile,
} from "@/services/proofEvidenceStorage"
import { getBackendModeLabel, submitEqProof } from "@/services/prooftreeBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  CheckCircle2,
  Link,
  LucideIcon,
  PlayCircle,
  Send,
  Sparkles,
  Upload,
  Video,
} from "lucide-react-native"
import { useRef, useState } from "react"
import { Animated, Platform, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const confetti = [
  { left: "18%", top: "18%", color: Colors.dark.primary },
  { left: "78%", top: "16%", color: Colors.dark.proof },
  { left: "28%", top: "42%", color: Colors.dark.growth },
  { left: "70%", top: "48%", color: Colors.dark.proof },
  { left: "48%", top: "24%", color: Colors.dark.skill },
] as const

export default function AddVideoProofScreen() {
  const auth = useAuth()
  const { skillId, level: initialLevel } = useLocalSearchParams<{
    skillId?: string
    level?: string
  }>()
  const skill = getSkill(skillId ?? "emotional_self_awareness")
  const domain = getDomain(skill.domainId)
  const color = domain?.color ?? Colors.dark.primary
  const [level, setLevel] = useState(initialLevel ?? "Level 3")
  const [videoSource, setVideoSource] = useState<"upload" | "link" | null>(null)
  const [evidenceFile, setEvidenceFile] = useState<EqEvidenceFile | null>(null)
  const [evidenceMessage, setEvidenceMessage] = useState("")
  const [reflection, setReflection] = useState("")
  const [submitMessage, setSubmitMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const activeCheckpoint =
    skill.levels.find((skillLevel) => skillLevel.title === level) ??
    skill.levels.find((skillLevel) => skillLevel.status === "In Progress") ??
    skill.levels[0]
  const scrollY = useRef(new Animated.Value(0)).current

  async function logProof() {
    if (!videoSource || isSubmitting) return
    if (videoSource === "upload" && !evidenceFile) {
      setEvidenceMessage("Choose an evidence file before logging this proof.")
      return
    }
    if (videoSource === "upload" && evidenceFile) {
      const validationError = validateEvidenceFile(evidenceFile)
      if (validationError) {
        setEvidenceMessage(validationError)
        return
      }
    }

    setIsSubmitting(true)
    const result = await submitEqProof({
      skillId: skill.id,
      skillIds: [skill.id],
      level,
      title: `${skill.title} proof`,
      reflection,
      evidenceKind: videoSource,
      evidenceFile: videoSource === "upload" ? evidenceFile : null,
      proofType: videoSource === "upload" ? "mixed" : "link",
    })
    let nextMessage = result.message
    if (reflection.trim() && (!auth.session || result.persisted)) {
      const journalResult = await createEqJournalEntry({
        title: `${skill.title} proof`,
        reflectionText: reflection,
        skillIds: [skill.id],
        proofId: result.persisted ? result.id : null,
        visibility: "private",
      })
      nextMessage = `${result.message} ${journalResult.message}`
    }
    setSubmitMessage(nextMessage)
    setSubmitted(!auth.session || result.persisted)
    setIsSubmitting(false)
  }

  if (submitted) {
    return (
      <SafeAreaView
        className="flex-1"
        edges={["top"]}
        style={{ backgroundColor: Colors.dark.surface }}
      >
        <View className="absolute inset-0" style={{ backgroundColor: Colors.dark.surface }} />
        {confetti.map((piece, index) => (
          <View
            key={index}
            className="absolute h-3 w-3 rounded-full"
            style={{
              left: piece.left,
              top: piece.top,
              backgroundColor: piece.color,
              shadowColor: piece.color,
              shadowOpacity: 0.5,
              shadowRadius: 10,
            }}
          />
        ))}
        <VStack className="flex-1 items-center justify-center px-6">
          <Box
            className="mb-6 h-28 w-28 items-center justify-center rounded-full border"
            style={{
              backgroundColor: `${color}22`,
              borderColor: `${color}88`,
              shadowColor: color,
              shadowOpacity: 0.35,
              shadowRadius: 24,
            }}
          >
            <CheckCircle2 color={color} size={56} />
          </Box>
          <Text className="text-center text-3xl font-bold text-white">Great work, coach!</Text>
          <Text className="mt-3 text-center leading-6" style={{ color: Colors.dark.textSecondary }}>
            {submitMessage ||
              "Your proof was captured. EMO.coach will keep local fallback status visible if Supabase sync is unavailable."}
          </Text>
          <Button
            size="xl"
            className="mt-8 w-full rounded-2xl"
            style={{ backgroundColor: Colors.dark.primary }}
            onPress={() => router.replace("/(tabs)/proof")}
          >
            <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
              Keep Coaching
            </ButtonText>
          </Button>
        </VStack>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow={`${activeCheckpoint.title} • ${activeCheckpoint.status}`}
        title={`New Proof: ${skill.title}`}
        subtitle={screenCopy.proof.subtitle}
        accentColor={color}
        backFallbackHref="/proof"
        statusText={getBackendModeLabel()}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 48 : 32,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Button
          size="xl"
          className="mb-5 rounded-2xl"
          style={{
            backgroundColor: Colors.dark.primary,
            shadowColor: Colors.dark.primary,
            shadowOpacity: 0.24,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 10 },
          }}
          onPress={() =>
            router.push({
              pathname: "/coach/[skillId]",
              params: { skillId: skill.id },
            })
          }
        >
          <Sparkles color={Colors.dark.primaryForeground} size={18} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            Coach this moment
          </ButtonText>
        </Button>

        <Box
          className="mb-5 h-64 overflow-hidden rounded-3xl border"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: videoSource ? `${color}AA` : `${color}44`,
          }}
        >
          <View
            className="absolute inset-0"
            style={{ backgroundColor: videoSource ? `${color}20` : "rgba(255,255,255,0.025)" }}
          />
          <VStack className="flex-1 items-center justify-center px-6">
            <Box
              className="mb-4 h-20 w-20 items-center justify-center rounded-full border"
              style={{
                backgroundColor: `${color}22`,
                borderColor: videoSource ? `${color}AA` : "rgba(255,255,255,0.08)",
              }}
            >
              {videoSource ? (
                <PlayCircle color={color} size={38} />
              ) : (
                <Video color={color} size={36} />
              )}
            </Box>
            <Text className="font-bold text-white">
              {videoSource ? "Proof ready to log" : "Proof preview"}
            </Text>
            <Text className="mt-1 text-center text-sm" style={{ color: Colors.dark.textSecondary }}>
              {videoSource === "upload"
                ? evidenceFile
                  ? `${evidenceFile.name} selected. Add a reflection when the moment is fresh.`
                  : "Choose a video, image, PDF, or text evidence file."
                : videoSource === "link"
                  ? "Link evidence selected. Your coach can help unpack what happened."
                  : "Add a short video, TikTok, or YouTube link"}
            </Text>
          </VStack>
        </Box>

        <HStack className="mb-6 gap-3">
          <ActionTile
            icon={Upload}
            label="Upload video"
            caption={evidenceFile?.name ?? "Select a file"}
            active={videoSource === "upload"}
            color={color}
            onPress={async () => {
              setVideoSource("upload")
              setEvidenceMessage("")
              const pickedFile = await pickProofEvidenceFile()
              if (!pickedFile) return
              setEvidenceFile(pickedFile)
              const validationError = validateEvidenceFile(pickedFile)
              setEvidenceMessage(validationError ?? "Evidence file ready to upload.")
            }}
          />
          <ActionTile
            icon={Link}
            label="Paste link"
            caption="TikTok or YouTube"
            active={videoSource === "link"}
            color={color}
            onPress={() => {
              setVideoSource("link")
              setEvidenceFile(null)
              setEvidenceMessage("")
            }}
          />
        </HStack>

        {evidenceMessage ? (
          <Box
            className="mb-5 rounded-2xl border p-4"
            style={{
              backgroundColor: `${Colors.dark.proof}14`,
              borderColor: `${Colors.dark.proof}38`,
            }}
          >
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
              {evidenceMessage}
            </Text>
          </Box>
        ) : null}

        <Text className="mb-3 font-bold text-white">Checkpoint level</Text>
        <HStack className="mb-6 flex-wrap items-center gap-2">
          {["Level 1", "Level 2", "Level 3", "Level 4"].map((item) => (
            <TouchableOpacity key={item} activeOpacity={0.84} onPress={() => setLevel(item)}>
              <Box
                className="rounded-full border px-4 py-3"
                style={{
                  ...filterChipStyle,
                  backgroundColor: level === item ? `${color}28` : Colors.dark.surfaceSecondary,
                  borderColor: level === item ? color : "rgba(255,255,255,0.07)",
                }}
              >
                <Text className="font-bold" style={{ color: level === item ? color : "white" }}>
                  {item}
                </Text>
              </Box>
            </TouchableOpacity>
          ))}
        </HStack>

        <Box
          className="mb-4 rounded-2xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.proof}14`,
            borderColor: `${Colors.dark.proof}38`,
          }}
        >
          <HStack className="items-start gap-3">
            <Sparkles color={Colors.dark.proof} size={18} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
              EMO.coach suggests: {screenCopy.proof.reflectionPrompt}
            </Text>
          </HStack>
        </Box>

        <Text className="mb-2 font-bold text-white">Reflection</Text>
        <Textarea
          size="lg"
          className="mb-6 h-44 rounded-2xl border"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${color}44`,
          }}
        >
          <TextareaInput
            className="p-4 text-white"
            placeholder={screenCopy.proof.reflectionPrompt}
            placeholderTextColor={Colors.dark.textSecondary}
            value={reflection}
            onChangeText={setReflection}
            multiline
          />
        </Textarea>

        <Button
          size="xl"
          className="rounded-2xl border"
          disabled={!videoSource || isSubmitting}
          style={{
            backgroundColor: "transparent",
            borderColor: `${Colors.dark.proof}88`,
            opacity: videoSource && !isSubmitting ? 1 : 0.54,
          }}
          onPress={logProof}
        >
          <Send color={Colors.dark.proof} size={18} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.proof }}>
            {isSubmitting ? "Logging..." : "Log Proof"}
          </ButtonText>
        </Button>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function ActionTile({
  icon: Icon,
  label,
  caption,
  active,
  color,
  onPress,
}: {
  icon: LucideIcon
  label: string
  caption: string
  active: boolean
  color: string
  onPress: () => void | Promise<void>
}) {
  return (
    <TouchableOpacity activeOpacity={0.86} className="flex-1" onPress={onPress}>
      <Box
        className="min-h-28 rounded-2xl border p-4"
        style={{
          backgroundColor: active ? `${color}22` : Colors.dark.surfaceSecondary,
          borderColor: active ? `${color}AA` : `${color}44`,
        }}
      >
        <HStack className="items-center gap-3">
          <Box
            className="h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: `${color}24` }}
          >
            <Icon color={color} size={20} />
          </Box>
          <VStack className="flex-1">
            <Text className="font-bold text-white">{label}</Text>
            <Text className="mt-1 text-xs leading-4" style={{ color: Colors.dark.textSecondary }}>
              {caption}
            </Text>
          </VStack>
        </HStack>
      </Box>
    </TouchableOpacity>
  )
}
